import { cn } from '../../lib/cn.js'
import { Input, type InputProps } from './Input.js'

export interface NumberInputProps extends Omit<InputProps, 'value' | 'onChange' | 'type'> {
  value: number | null
  onChange: (value: number | null) => void
  min?: number
  max?: number
  /** The words for an empty field — 9.6a's "a cap or none". */
  emptyMeans?: string
  /**
   * The unit, printed inside the field.
   *
   * Some of these numbers are meaningless without it. A question's time limit runs
   * from 5 to 600 and is stored in **seconds**, so a box reading `45` beside a
   * platform whose every other duration is in minutes is a box that will be filled
   * in wrong — by somebody setting a forty-five *minute* timer on a poll.
   *
   * Inside the field rather than in the hint underneath, because the hint is where
   * the rule goes and this is part of the value.
   */
  unit?: string
}

/**
 * A whole number, and the difference between none and nought.
 *
 * 9.6a asks for *"a cap or none"*, and those are not the same as a cap of zero:
 * no cap means anybody may enrol, and a cap of zero means nobody may. A plain
 * number field cannot express the first, which is how a class ends up closed to
 * everyone. Empty is null here, and `emptyMeans` says so under the field.
 */
export function NumberInput({
  value,
  onChange,
  min,
  max,
  emptyMeans,
  unit,
  className,
  ...props
}: NumberInputProps) {
  const field = (
    <Input
      inputMode="numeric"
      placeholder={emptyMeans}
      value={value === null ? '' : String(value)}
      onChange={(e) => {
        const raw = e.target.value.trim()
        if (raw === '') return onChange(null)
        const n = Number(raw.replace(/[^0-9-]/g, ''))
        if (Number.isNaN(n)) return
        if (min !== undefined && n < min) return onChange(min)
        if (max !== undefined && n > max) return onChange(max)
        onChange(n)
      }}
      className={unit ? cn('pr-14', className) : className}
      {...props}
    />
  )

  if (!unit) return field
  return (
    <div className="relative flex items-center">
      {field}
      <span
        className="pointer-events-none absolute right-3 text-second text-ink-muted"
        // Read out as part of the field's own name, not as a stray word after it.
        aria-hidden
      >
        {unit}
      </span>
    </div>
  )
}
